/**
 * 助手对话页（/assistant，与 iOS AssistantView 一致，网页版只有文字输入）：
 * 消息气泡、Markdown 回复、可折叠的思考过程、工具标签、错误；加载更早的记录；空时显示示例问题；
 * 输入框（Enter 发送、Shift+Enter 换行）、停止按钮；附件（最多 4 张图片、5 个文件，可选择或拖进来）。
 */
import { Brain, ChevronRight, CircleAlert, CircleCheck, CircleMinus, CircleX, FileText, Image as ImageIcon, Paperclip, Plus, Send, Settings, Square, X } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState, type DragEvent, type KeyboardEvent } from "react";
import { useNavigate } from "react-router";
import { devParam, takeOnce, toast, useCurrentChild } from "../../core";
import { Button, IconButton, Spinner, StarMascot } from "../../ui";
import { loadHistory, loadOlder, send, stop, useAgent, type Item } from "./agent";
import { ACCEPT, isImageFile, MAX_FILES, MAX_IMAGES, prepareFile, type PreparedAttachment } from "./attachments";
import { ATTACHMENT_MARK } from "./context";
import { Markdown } from "./Markdown";
import { chatRoute, refreshCredentials, useCreds, usePrefs } from "./settings";
import { THINKING_LEVELS, supportsThinking } from "./providers";
import "./assistant.css";

export function ChatPage() {
  const navigate = useNavigate();
  const items = useAgent((s) => s.items);
  const busy = useAgent((s) => s.busy);
  const uploading = useAgent((s) => s.uploading);
  const hasOlder = useAgent((s) => s.hasOlder);
  const loadingOlder = useAgent((s) => s.loadingOlder);
  const historyLoaded = useAgent((s) => s.historyLoaded);
  const loadingHistory = useAgent((s) => s.loadingHistory);
  const liveThinkingId = useAgent((s) => s.liveThinkingId);
  const creds = useCreds();
  const prefs = usePrefs();
  const route = chatRoute(prefs, creds);

  const [text, setText] = useState("");
  const [pending, setPending] = useState<PreparedAttachment[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const stickToBottom = useRef(true);
  const olderAnchor = useRef<number | null>(null);

  useEffect(() => {
    void refreshCredentials();
    void loadHistory();
  }, []);

  // 调试：?dev_assistant=文字 打开后直接提问（等历史加载完）
  useEffect(() => {
    const q = devParam("dev_assistant");
    if (q && historyLoaded && takeOnce("dev_assistant")) send(q);
  }, [historyLoaded]);

  // 新内容出现时滚到底部（用户往上翻看时不打扰）；加载更早的记录时保持当前位置
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (olderAnchor.current != null) {
      el.scrollTop = el.scrollHeight - olderAnchor.current;
      olderAnchor.current = null;
    } else if (stickToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [items]);

  const onScroll = () => {
    const el = scroller.current;
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const older = () => {
    const el = scroller.current;
    if (el) olderAnchor.current = el.scrollHeight - el.scrollTop;
    void loadOlder();
  };

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    setPreparing(true);
    const next = [...pending];
    for (const f of files) {
      const image = isImageFile(f);
      const count = next.filter((p) => (p.kind === "image") === image).length;
      if (image && count >= MAX_IMAGES) {
        toast(`最多 ${MAX_IMAGES} 张图片`, { error: true });
        continue;
      }
      if (!image && count >= MAX_FILES) {
        toast(`最多 ${MAX_FILES} 个文件`, { error: true });
        continue;
      }
      try {
        next.push(await prepareFile(f));
      } catch (e) {
        toast(e instanceof Error ? e.message : String(e), { error: true });
      }
    }
    setPending(next);
    setPreparing(false);
  };

  const submit = (q = text) => {
    if (preparing || (!q.trim() && !pending.length)) return;
    stickToBottom.current = true;
    send(q, pending);
    setText("");
    setPending([]);
  };

  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void addFiles(Array.from(e.dataTransfer.files));
  };

  const hasKey = creds !== false;
  const thinkingOn = supportsThinking(route.provider, route.model) && prefs.thinking !== "off";
  const status = preparing ? "正在处理附件…" : uploading ? "正在上传附件…" : busy ? "正在处理…（点按钮停止）" : "";

  return (
    <div
      className="asst-page"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false);
      }}
      onDrop={onDrop}
    >
      <header className="asst-header">
        <StarMascot size={30} animated={busy} />
        <div className="asst-header-title">
          <h1>助手</h1>
          <span>
            {route.provider.name} · {route.model}
            {thinkingOn ? ` · 思考${THINKING_LEVELS.find((l) => l.id === prefs.thinking)?.name ?? ""}` : ""}
          </span>
        </div>
        <IconButton label="助手设置" onClick={() => navigate("/assistant/settings")}>
          <Settings size={18} strokeWidth={2.4} />
        </IconButton>
      </header>

      <div className="asst-scroll" ref={scroller} onScroll={onScroll}>
        <div className="asst-list">
          {creds === false && <KeyMissing onSettings={() => navigate("/assistant/settings")} />}
          {hasOlder && (
            <button type="button" className="asst-older" onClick={older} disabled={loadingOlder}>
              {loadingOlder ? <Spinner size={14} /> : "加载更早的记录"}
            </button>
          )}
          {loadingHistory && !items.length && (
            <div className="asst-loading">
              <Spinner size={18} />
            </div>
          )}
          {historyLoaded && !items.length && hasKey && <Suggestions onPick={(q) => submit(q)} />}
          {items.map((it) => (
            <Row key={it.id} item={it} live={it.id === liveThinkingId} />
          ))}
          {busy && !liveThinkingId && items[items.length - 1]?.kind === "user" && (
            <div className="asst-typing" aria-label="正在处理">
              <i />
              <i />
              <i />
            </div>
          )}
        </div>
      </div>

      <footer className="asst-composer">
        {status && <div className="asst-status">{status}</div>}
        {pending.length > 0 && (
          <div className="asst-pending">
            {pending.map((p, i) => (
              <span key={`${p.name}-${i}`} className="asst-pending-chip">
                {p.kind === "image" ? <ImageIcon size={15} /> : <FileText size={15} />}
                <span>{p.name}</span>
                <button type="button" aria-label="移除" onClick={() => setPending(pending.filter((_, j) => j !== i))}>
                  <X size={14} strokeWidth={2.6} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="asst-inputbar">
          <IconButton label="添加图片或文件" onClick={() => fileInput.current?.click()} disabled={preparing}>
            <Plus size={20} strokeWidth={2.6} />
          </IconButton>
          <input
            ref={fileInput}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = "";
              void addFiles(files);
            }}
          />
          <textarea
            ref={input}
            className="asst-input"
            rows={1}
            value={text}
            placeholder={hasKey ? "输入问题，Enter 发送，Shift+Enter 换行" : "先在设置里填写百炼 API Key"}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKey}
            onPaste={(e) => {
              const files = Array.from(e.clipboardData.files);
              if (files.length) {
                e.preventDefault();
                void addFiles(files);
              }
            }}
          />
          {busy ? (
            <IconButton label="停止" className="asst-send is-stop" onClick={stop}>
              <Square size={16} fill="currentColor" />
            </IconButton>
          ) : (
            <IconButton label="发送" className="asst-send" onClick={() => submit()} disabled={preparing || (!text.trim() && !pending.length)}>
              <Send size={18} strokeWidth={2.4} />
            </IconButton>
          )}
        </div>
      </footer>

      {dragging && (
        <div className="asst-drop">
          <Paperclip size={28} />
          <span>松开即可添加图片或文件</span>
        </div>
      )}
    </div>
  );
}

function Row({ item, live }: { item: Item; live: boolean }) {
  switch (item.kind) {
    case "user":
      return <UserBubble text={item.text} />;
    case "assistant":
      return item.text.trim() ? (
        <div className="asst-bubble is-reply">
          <Markdown text={item.text} />
        </div>
      ) : null;
    case "thinking":
      return <ThinkingRow text={item.text} live={live} />;
    case "tool":
      return (
        <span className="asst-tool">
          {item.state === "running" && <Spinner size={13} />}
          {item.state === "done" && <CircleCheck size={15} color="var(--kp-success)" />}
          {item.state === "failed" && <CircleX size={15} color="var(--kp-danger)" />}
          {item.state === "cancelled" && <CircleMinus size={15} color="var(--kp-ink-muted)" />}
          {item.title}
        </span>
      );
    case "error":
      return (
        <div className="asst-error" role="alert">
          <CircleAlert size={16} />
          <span>{item.text}</span>
        </div>
      );
  }
}

/** 用户消息：附件名显示成回形针标签 */
function UserBubble({ text }: { text: string }) {
  const lines = text.split("\n");
  const files = lines.filter((l) => l.startsWith(ATTACHMENT_MARK)).map((l) => l.slice(ATTACHMENT_MARK.length));
  const body = lines.filter((l) => !l.startsWith(ATTACHMENT_MARK)).join("\n");
  return (
    <div className="asst-bubble is-mine">
      {body && <p>{body}</p>}
      {files.map((f, i) => (
        <span key={i} className="asst-file">
          <Paperclip size={13} />
          {f}
        </span>
      ))}
    </div>
  );
}

/** 思考过程：思考中展开显示，结束后折叠成一行，点开查看全文 */
function ThinkingRow({ text, live }: { text: string; live: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const open = expanded || live;
  return (
    <div className="asst-thinking">
      <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={open}>
        <Brain size={15} />
        <span>{live ? "正在思考…" : "思考过程"}</span>
        <ChevronRight size={13} strokeWidth={3} className={open ? "is-open" : ""} />
      </button>
      {open && <p>{text.trim()}</p>}
    </div>
  );
}

function KeyMissing({ onSettings }: { onSettings: () => void }) {
  return (
    <div className="asst-card">
      <h3>先设置百炼 API Key</h3>
      <p>对话默认使用阿里云百炼。在百炼控制台（北京地域）创建 API Key，填到设置里即可，全家共用。</p>
      <Button variant="accentPill" height={40} onClick={onSettings}>
        去设置
      </Button>
    </div>
  );
}

function Suggestions({ onPick }: { onPick: (q: string) => void }) {
  const name = useCurrentChild()?.name ?? "孩子";
  const list = [`${name}今天有什么安排？`, `明天下午四点给${name}安排一节钢琴课，提前十分钟提醒`, `给${name}加 5 分，原因是主动收拾书包`, `发个提醒给${name}：下楼吃饭啦`];
  return (
    <div className="asst-suggest">
      <p>可以这样问我，也可以发一张课程表图片让我导入：</p>
      {list.map((q) => (
        <button key={q} type="button" onClick={() => onPick(q)}>
          {q}
        </button>
      ))}
    </div>
  );
}
